import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Button,
  Paper,
  Chip,
  Avatar
} from '@mui/material';
import {
  Palette as PaletteIcon,
  FormatSize as FontIcon,
  ViewQuilt as LayoutIcon,
  DarkMode as DarkModeIcon,
  PhoneAndroid as MobileIcon,
  Image as LogoIcon,
  Widgets as WidgetIcon,
  Speed as SpeedIcon,
  Accessibility as AccessibilityIcon,
  Language as LanguageIcon,
  CheckCircle as CheckIcon,
  Brush as BrushIcon,
  ColorLens as ColorIcon,
  TextFields as TextIcon,
  Apps as AppsIcon,
  LightMode as LightModeIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';


import { useState } from 'react';

const ThemeDesignPage = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  // Tema Özellikleri
  const themeFeatures = [
    {
      icon: <ColorIcon sx={{ fontSize: 50 }} />,
      title: "Renk Paleti",
      description: "Markanıza uygun renk şeması oluşturun",
      color: "#ec4899",
      gradient: "linear-gradient(135deg, #ec4899 0%, #f472b6 100%)",
      items: [
        "Ana renk seçimi",
        "İkincil renk paleti",
        "Vurgu renkleri",
        "Arkaplan renkleri",
        "Metin renk ayarları"
      ]
    },
    {
      icon: <TextIcon sx={{ fontSize: 50 }} />,
      title: "Font Yönetimi",
      description: "Google Fonts entegrasyonu ile tipografi",
      color: "#1e40af",
      gradient: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
      items: [
        "Google Fonts kütüphanesi",
        "Özel font yükleme",
        "Font boyut ayarları",
        "Satır yüksekliği kontrolü",
        "Font ağırlığı seçenekleri"
      ]
    },
    {
      icon: <LayoutIcon sx={{ fontSize: 50 }} />,
      title: "Layout Ayarları",
      description: "Sayfa düzeni ve görünüm kontrolü",
      color: "#059669",
      gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
      items: [
        "Sayfa genişliği ayarı",
        "Container yapısı",
        "Spacing sistemleri",
        "Border radius ayarları",
        "Shadow/Gölge efektleri"
      ]
    },
    {
      icon: <DarkModeIcon sx={{ fontSize: 50 }} />,
      title: "Açık/Koyu Mod",
      description: "Otomatik tema geçişi desteği",
      color: "#7c3aed",
      gradient: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
      items: [
        "Light/Dark mode",
        "Otomatik geçiş",
        "Kullanıcı tercihi kaydetme",
        "Sistem teması algılama",
        "Özel tema renkleri"
      ]
    },
    {
      icon: <MobileIcon sx={{ fontSize: 50 }} />,
      title: "Responsive Tasarım",
      description: "Her cihazda mükemmel görünüm",
      color: "#ea580c",
      gradient: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)",
      items: [
        "Mobile-first yaklaşım",
        "Tablet optimizasyonu",
        "Desktop görünümü",
        "Breakpoint yönetimi",
        "Touch-friendly arayüz"
      ]
    },
    {
      icon: <LogoIcon sx={{ fontSize: 50 }} />,
      title: "Marka Kimliği",
      description: "Logo ve görsel kimlik yönetimi",
      color: "#0891b2",
      gradient: "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)",
      items: [
        "Logo yükleme ve yönetimi",
        "Favicon ayarları",
        "Sosyal medya görselleri",
        "E-posta şablonları",
        "Watermark ayarları"
      ]
    }
  ];

  // Tasarım Özellikleri
  const designFeatures = [
    {
      icon: <WidgetIcon sx={{ fontSize: 45 }} />,
      title: "70+ Widget",
      description: "Sınırsız tasarım özgürlüğü",
      color: "#1e40af",
      stats: "70+ Widget"
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 45 }} />,
      title: "Hızlı Yükleme",
      description: "Optimize edilmiş performans",
      color: "#dc2626",
      stats: "<1s Yükleme"
    },
    {
      icon: <AccessibilityIcon sx={{ fontSize: 45 }} />,
      title: "Erişilebilirlik",
      description: "WCAG standartları",
      color: "#059669",
      stats: "WCAG 2.1"
    },
    {
      icon: <LanguageIcon sx={{ fontSize: 45 }} />,
      title: "Çoklu Dil",
      description: "i18n desteği",
      color: "#7c3aed",
      stats: "10+ Dil"
    },
    {
      icon: <BrushIcon sx={{ fontSize: 45 }} />,
      title: "Pixel Perfect",
      description: "Detaylı tasarım kontrolü",
      color: "#ea580c",
      stats: "100% Hassas"
    },
    {
      icon: <AppsIcon sx={{ fontSize: 45 }} />,
      title: "Modüler Yapı",
      description: "Esnek ve genişletilebilir",
      color: "#0891b2",
      stats: "Modüler"
    }
  ];

  // Renk Paleti Örnekleri
  const colorPalettes = [
    { name: "Mavi Tonları", colors: ["#1e40af", "#3b82f6", "#60a5fa", "#93c5fd"] },
    { name: "Kırmızı Tonları", colors: ["#dc2626", "#ef4444", "#f87171", "#fca5a5"] },
    { name: "Yeşil Tonları", colors: ["#059669", "#10b981", "#34d399", "#6ee7b7"] },
    { name: "Mor Tonları", colors: ["#7c3aed", "#8b5cf6", "#a78bfa", "#c4b5fd"] }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box 
             sx={{
          py: { xs: 10, md: 16 },
          background: `
            linear-gradient(
              135deg,
              #ec4899 0%,
              #f472b6 50%,
              #fb923c 100%
            )
          `,
          position: 'relative',
          overflow: 'hidden',
          color: 'white',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.15) 0%, transparent 50%)
            `,
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                mb: 4,
                mx: 'auto'
              }}
            >
              <PaletteIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>

            <Typography
              variant="overline"
              sx={{
                fontSize: '0.9rem',
                fontWeight: 900,
                letterSpacing: '0.15em',
                color: 'rgba(255,255,255,0.9)',
                mb: 2,
                display: 'block',
                textTransform: 'uppercase'
              }}
            >
              TEMA VE TASARIM SİSTEMİ
            </Typography>

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4.5rem' },
                fontWeight: 800,
                mb: 4,
                color: 'white',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                lineHeight: 1.1
              }}
            >
              Markanıza Özel Tasarım
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                color: 'rgba(255,255,255,0.9)',
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.6,
                fontWeight: 400,
                mb: 6
              }}
            >
              Renk paleti, font yönetimi, layout ayarları ve daha fazlası ile 
              markanıza özgü görsel kimlik oluşturun
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: 'white',
                  color: '#ec4899',
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    background: '#f8fafc',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Tema Editörü
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  borderWidth: 2,
                  textTransform: 'none',
                  '&:hover': {
                    borderWidth: 2,
                    background: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Örnek Temalar
              </Button>
            </Stack>
          </Box>
        </Container>
    {/* Dalga Geçişi */}
    <Box
          sx={{
            position: 'absolute',
            bottom: '-130px',
            left: 0,
            width: '100%',
            overflow: 'hidden',
            lineHeight: 0
          }}
        >
          <svg
            viewBox="0 0 1440 400"
            preserveAspectRatio="none"
            style={{ width: '100%', height: '250px', display: 'block' }}
          >
            <path
              d="M0,160L48,170.7C96,181,192,203,288,197.3C384,192,480,160,576,154.7C672,149,768,171,864,181.3C960,192,1056,192,1152,176C1248,160,1344,128,1392,112L1440,96L1440,400L0,400Z"
              fill="#f8f9fa"
            />
          </svg>
        </Box>
      </Box>

      {/* Tema Özellikleri */}
      <Box sx={{ py: { xs: 10, md: 16 }, background: '#f8fafc' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                background: 'linear-gradient(135deg, #1e293b 0%, #ec4899 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Özelleştirilebilir Tema Sistemi
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#64748b',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Her detayı kontrol altına alın
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {themeFeatures.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Paper
                  elevation={0}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    p: 5,
                    height: '100%',
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: feature.gradient,
                      transform: hoveredCard === index ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease'
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 40px ${feature.color}20`,
                      borderColor: feature.color
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      background: `${feature.color}15`,
                      color: feature.color,
                      mb: 3,
                      transition: 'all 0.4s ease',
                      transform: hoveredCard === index ? 'scale(1.1) rotate(5deg)' : 'scale(1)'
                    }}
                  >
                    {feature.icon}
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: '#1e293b',
                      fontSize: '1.3rem'
                    }}
                  >
                    {feature.title}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#64748b',
                      lineHeight: 1.7,
                      mb: 3
                    }}
                  >
                    {feature.description}
                  </Typography>

                  <List dense>
                    {feature.items.map((item, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon sx={{ color: feature.color, fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          primaryTypographyProps={{
                            fontSize: '0.95rem',
                            color: '#475569',
                            fontWeight: 500
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Renk Paleti Showcase */}
      <Box sx={{ py: { xs: 10, md: 16 }, background: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                background: 'linear-gradient(135deg, #ec4899 0%, #fb923c 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Renk Paleti Örnekleri
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#64748b',
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Hazır renk şemaları veya özel paletinizi oluşturun
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {colorPalettes.map((palette, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#1e293b' }}>
                    {palette.name}
                  </Typography>
                  <Stack spacing={1.5}>
                    {palette.colors.map((color, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2
                        }}
                      >
                        <Box
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: 2,
                            background: color,
                            boxShadow: `0 4px 12px ${color}40`
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 600,
                            color: '#64748b'
                          }}
                        >
                          {color}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Tasarım Özellikleri */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
          color: 'white'
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                color: 'white'
              }}
            >
              Tasarım Özellikleri
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Modern ve performanslı tasarım standartları
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {designFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    p: 4,
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 3,
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.08)',
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 70,
                      height: 70,
                      borderRadius: 2,
                      background: `${feature.color}30`,
                      color: feature.color,
                      mb: 3
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
                    {feature.description}
                  </Typography>
                  <Chip
                    label={feature.stats}
                    sx={{
                      background: feature.color,
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="md">
      <Box
        sx={{
          my: 10,
          background: `
          linear-gradient(
            135deg,
            #ec4899 0%,
            #f472b6 50%,
            #fb923c 100%
          )
        `,
          py: { xs: 2, md: 3 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Dekoratif Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '20px 20px',
            opacity: 0.5
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                color: 'white',
                mb: 3
              }}
            >
              Kendi Tasarımınızı Oluşturun
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                mb: 5,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Markanıza özgü, profesyonel ve modern bir haber sitesi tasarlayın
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              sx={{ justifyContent: 'center' }}
            >
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowIcon />}
                sx={{
                  background: 'white',
                  color: '#ff6b35',
                  px: 6,
                  py: 2.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: '50px',
                  textTransform: 'none',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  '&:hover': {
                    background: '#f8f9fa',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.3)'
                  }
                }}
              >
                Ücretsiz Başla
              </Button>

              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 6,
                  py: 2.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: '50px',
                  textTransform: 'none',
                  borderWidth: 2,
                  '&:hover': {
                    background: 'rgba(255,255,255,0.1)',
                    borderColor: 'white',
                    borderWidth: 2
                  }
                }}
              >
                Demo Talep Et
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>
      </Container>

    </Box>
  );
};

export default ThemeDesignPage;

